import { zodResolver } from '@hookform/resolvers/zod';
import { type UpdateFamilyRequest, updateFamilyRequestSchema } from '@familywallet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CurrencyLockNotice } from '../components/CurrencyLockNotice';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { meQueryKey, useCan } from '../lib/auth';
import { familyKey } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';

function timeZones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [];
  }
}

export function FamilyPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canManage = useCan('family.manage');
  const invalidateLedger = useInvalidateLedger();
  const family = useQuery({ queryKey: familyKey, queryFn: () => api.family.get() });
  const form = useForm<UpdateFamilyRequest>({
    resolver: zodResolver(updateFamilyRequestSchema),
    values: family.data
      ? {
          name: family.data.name,
          baseCurrency: family.data.baseCurrency,
          timezone: family.data.timezone,
        }
      : undefined,
  });
  const save = useMutation({
    mutationFn: (v: UpdateFamilyRequest) => api.family.update(v),
    onSuccess: async (updated) => {
      queryClient.setQueryData(familyKey, updated);
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      // Accounts follow a currency change made before the first transaction.
      await invalidateLedger();
    },
  });

  if (family.isPending) return <p role="status">{t('app.loading')}</p>;
  if (family.isError) return <FormError error={family.error} />;

  const errors = form.formState.errors;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('family.title')} back="/family" />
      {!canManage && <p className="text-muted">{t('family.readOnly')}</p>}
      <Card>
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
        >
          <FormError error={save.error} />
          <fieldset disabled={!canManage} className="flex flex-col gap-4">
            <Field label={t('fields.familyName')} error={errors.name}>
              <Input {...form.register('name')} />
            </Field>
            <Field
              label={t('fields.baseCurrency')}
              hint={
                family.data.currencyLocked ? t('currency.locked') : t('fields.baseCurrencyHint')
              }
              error={errors.baseCurrency}
              kind="currency"
            >
              <Input
                disabled={family.data.currencyLocked}
                autoCapitalize="characters"
                maxLength={3}
                dir="ltr"
                {...form.register('baseCurrency', { setValueAs: (v: string) => v.toUpperCase() })}
              />
            </Field>
            <Field
              label={t('fields.timezone')}
              hint={t('fields.timezoneHint')}
              error={errors.timezone}
              kind="timezone"
            >
              <Input list="timezones" dir="ltr" {...form.register('timezone')} />
            </Field>
            <datalist id="timezones">
              {timeZones().map((zone) => (
                <option key={zone} value={zone} />
              ))}
            </datalist>
          </fieldset>
          {canManage && <CurrencyLockNotice />}
          {canManage && (
            <Button type="submit" disabled={save.isPending}>
              {save.isSuccess && !form.formState.isDirty ? t('common.saved') : t('common.save')}
            </Button>
          )}
        </form>
      </Card>
    </div>
  );
}
